import { arrowGeometry } from './markupGeometry.ts';
import type {
    ArrowShape,
    EllipseShape,
    MarkupShape,
    PenShape,
    Point,
    TextShape
} from './markupShapes.ts';
import { textFont } from './markupStyle.ts';

export interface MarkupImage {
    width: number;
    height: number;
}

type Context2D = CanvasRenderingContext2D;

function strokeSegment(ctx: Context2D, from: Point, to: Point): void {
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
}

function drawPen(ctx: Context2D, shape: PenShape): void {
    const [first, ...rest] = shape.points;
    if (!first) return;
    if (rest.length === 0) {
        ctx.beginPath();
        ctx.arc(first.x, first.y, shape.width / 2, 0, Math.PI * 2);
        ctx.fill();
        return;
    }
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    rest.forEach((point) => ctx.lineTo(point.x, point.y));
    ctx.stroke();
}

function drawArrow(ctx: Context2D, shape: ArrowShape): void {
    const geometry = arrowGeometry(shape.from, shape.to, shape.width);
    if (!geometry) return;
    strokeSegment(ctx, shape.from, geometry.shaftEnd);
    ctx.beginPath();
    ctx.moveTo(geometry.tip.x, geometry.tip.y);
    ctx.lineTo(geometry.left.x, geometry.left.y);
    ctx.lineTo(geometry.right.x, geometry.right.y);
    ctx.closePath();
    ctx.fill();
}

function drawEllipse(ctx: Context2D, shape: EllipseShape): void {
    const rx = shape.box.width / 2;
    const ry = shape.box.height / 2;
    ctx.beginPath();
    ctx.ellipse(shape.box.x + rx, shape.box.y + ry, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
}

// Plain fill only: a halo or shadow reads as a smudge on the photo.
function drawText(ctx: Context2D, shape: TextShape): void {
    ctx.font = textFont(shape.fontSize);
    ctx.textBaseline = 'top';
    ctx.fillStyle = shape.color;
    ctx.fillText(shape.text, shape.at.x, shape.at.y);
}

function drawStroked(ctx: Context2D, shape: Exclude<MarkupShape, TextShape>): void {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = shape.width;
    ctx.strokeStyle = shape.color;
    ctx.fillStyle = shape.color;
    switch (shape.kind) {
        case 'pen':
            return drawPen(ctx, shape);
        case 'arrow':
            return drawArrow(ctx, shape);
        case 'line':
            return strokeSegment(ctx, shape.from, shape.to);
        case 'rect':
            return ctx.strokeRect(shape.box.x, shape.box.y, shape.box.width, shape.box.height);
        case 'ellipse':
            return drawEllipse(ctx, shape);
    }
}

export function drawShape(ctx: Context2D, shape: MarkupShape): void {
    ctx.save();
    if (shape.kind === 'text') drawText(ctx, shape);
    else drawStroked(ctx, shape);
    ctx.restore();
}

// The caller sets the image-to-target transform, so screen and export share this one path.
export function drawScene(
    ctx: Context2D,
    image: CanvasImageSource & MarkupImage,
    shapes: readonly MarkupShape[]
): void {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, image.width, image.height);
    shapes.forEach((shape) => drawShape(ctx, shape));
}

// What the canvas paints: committed marks, with the live one swapped in or stacked on top.
export function sceneShapes(
    shapes: readonly MarkupShape[],
    preview: MarkupShape | null,
    hiddenId: string | null
): readonly MarkupShape[] {
    const visible = hiddenId ? shapes.filter((shape) => shape.id !== hiddenId) : shapes;
    if (!preview) return visible;
    const replaces = visible.some((shape) => shape.id === preview.id);
    return replaces
        ? visible.map((shape) => (shape.id === preview.id ? preview : shape))
        : [...visible, preview];
}
